import { getPreviousPathname, isReturnFromSubRoute } from '@client/hooks/navigation/previous-pathname';
import { hasStoredPathState, usePathState } from '@client/hooks/navigation/usePathState';
import { debounce } from '@client/utils';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isFilterValueEmpty, resolveFieldDefaults, transformValueForOperator } from '../core/operators';
import type { Filter, FilterBuilderState, FilterConfig, FilterValue } from '../core/types';
import { buildWhereClause, isWhereClauseEmpty } from '../core/where-builder';

/**
 * Build initial where clause from URL filters
 * Use this to initialize queryParams.where to prevent flickering when navigating with filters
 *
 * @param config - Filter configuration
 * @param initialFilters - Filters from URL
 * @returns Where clause for initial query
 *
 * @example
 * ```tsx
 * const searchParams = Route.useSearch();
 * const initialWhere = buildInitialWhereFromFilters(filterConfig, searchParams['incident-filters']);
 *
 * const [queryParams, setQueryParams] = usePathState('query-params', {
 *   limit: DEFAULT_PAGE_SIZE,
 *   orderBy: { id: 'desc' },
 *   where: initialWhere, // Use computed initial where
 * });
 * ```
 */
export function buildInitialWhereFromFilters<TWhere>(config: FilterConfig<TWhere>, initialFilters?: Filter[]): TWhere {
    if (!initialFilters || initialFilters.length === 0) {
        return {} as TWhere;
    }
    return buildWhereClause(initialFilters, config);
}

/**
 * Filter Builder Hooks
 *
 * React hooks for managing filter state, building Prisma where clauses,
 * and optionally persisting filters to URL query parameters.
 *
 * @module useFilterBuilder
 */

/**
 * Whether a filter has a meaningful value (would contribute a where condition). The inverse of
 * the canonical `isFilterValueEmpty`, so the debounced onChange gating matches what
 * `buildWhereClause` actually emits.
 */
function hasFilterMeaningfulValue(filter: Filter): boolean {
    return !isFilterValueEmpty(filter.operator, filter.value);
}

/** Unique per-instance filter id. */
function generateFilterId(): string {
    return `filter_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Whitespace-split search terms from all non-empty `textSearch` filters. */
function computeSearchTerms<TWhere>(filters: Filter[], config: FilterConfig<TWhere>): string[] {
    const terms: string[] = [];
    for (const filter of filters) {
        const fieldConfig = config.filters.find((f) => f.id === filter.filterId);
        if (fieldConfig?.type === 'textSearch' && typeof filter.value === 'string' && filter.value.trim()) {
            terms.push(...filter.value.trim().split(' '));
        }
    }
    return terms;
}

/**
 * Hook for managing filter state and building where clauses
 *
 * Core hook that manages the filter state, handles filter operations,
 * and automatically generates Prisma where clauses with debouncing.
 *
 * @template TWhere - The Prisma where input type
 * @param config - Filter configuration with field definitions
 * @param initialFilters - Optional initial filter state
 * @returns Filter builder state with methods and current where clause
 *
 * @example
 * ```typescript
 * function UserList() {
 *   const filterConfig = createFilterConfig<UserWhere>([
 *     commonFields.text({ id: 'name', label: 'Name', dbField: 'name' }),
 *     commonFields.select({
 *       id: 'status',
 *       label: 'Status',
 *       options: statusOptions,
 *       dbField: 'status'
 *     })
 *   ]);
 *
 *   const { filters, addFilter, whereClause } = useFilterBuilder(filterConfig);
 *
 *   const { data } = trpc.user.list.useQuery({ where: whereClause });
 *
 *   return (
 *     <FilterBar
 *       config={filterConfig}
 *       filters={filters}
 *       onAddFilter={addFilter}
 *       // ... other props
 *     />
 *   );
 * }
 * ```
 */
export function useFilterBuilder<TWhere>(
    config: FilterConfig<TWhere>,
    initialFilters: Filter[] = [],
    onChange?: (whereClause: TWhere, searchTerms?: string[], interacted?: boolean) => void,
): FilterBuilderState<TWhere> {
    // Whether the user has modified the filters since mount (via any of the mutators below).
    // `false` only for the mount-time replay of the initial/restored filters — consumers use this
    // to tell a restore replay apart from a real user change (see useFilterBuilderWithUrl).
    const interactedRef = useRef(false);
    const [filters, setFiltersState] = useState<Filter[]>(() => {
        // Seed filters for fields with a default value or pinned fields (placeholder).
        const filtersToAdd: Filter[] = [...initialFilters];
        for (const fieldConfig of config.filters) {
            if (fieldConfig.defaultValue === undefined && fieldConfig.pinned !== true) continue;
            if (initialFilters.some((f) => f.filterId === fieldConfig.id)) continue;
            const { operator, value } = resolveFieldDefaults(fieldConfig, { preferContains: true });
            filtersToAdd.push({ id: generateFilterId(), filterId: fieldConfig.id, operator, value });
        }
        return filtersToAdd;
    });

    // Add a new filter
    const addFilter = useCallback(
        (filterId: string): string | undefined => {
            const fieldConfig = config.filters.find((f) => f.id === filterId);
            if (!fieldConfig) {
                console.warn(`No configuration found for filter: ${filterId}`);
                return undefined;
            }

            const { operator, value } = resolveFieldDefaults(fieldConfig, { preferContains: true });
            const newFilter: Filter = { id: generateFilterId(), filterId, operator, value };

            interactedRef.current = true;
            setFiltersState((prev) => [...prev, newFilter]);

            // Return the new filter ID so the UI can focus on it
            return newFilter.id;
        },
        [config.filters],
    );

    // Update an existing filter
    const updateFilter = useCallback(
        (id: string, updates: Partial<Filter>) => {
            interactedRef.current = true;
            setFiltersState((prev) =>
                prev.map((filter) => {
                    if (filter.id !== id) return filter;

                    const updated = { ...filter, ...updates };

                    // If operator changed, intelligently transform value
                    // Skip when field changed — the caller already provides the correct default value
                    const fieldChanged = updates.filterId && updates.filterId !== filter.filterId;
                    if (!fieldChanged && updates.operator && updates.operator !== filter.operator) {
                        const fieldConfig = config.filters.find((f) => f.id === filter.filterId);
                        const fieldType = fieldConfig?.type || 'text';
                        updated.value = transformValueForOperator(
                            filter.value,
                            filter.operator,
                            updates.operator,
                            fieldType,
                        ) as FilterValue;
                    }

                    return updated;
                }),
            );
        },
        [config.filters],
    );

    // Remove a filter
    const removeFilter = useCallback((id: string) => {
        interactedRef.current = true;
        setFiltersState((prev) => prev.filter((filter) => filter.id !== id));
    }, []);

    // Clear all filters
    const clearFilters = useCallback(() => {
        interactedRef.current = true;
        setFiltersState([]);
    }, []);

    // Build where clause from current filters with debounce
    const [whereClause, setWhereClause] = useState<TWhere>(() => buildWhereClause(initialFilters, config));

    // Store onChange in a ref to avoid recreating the debounced function
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Store config in a ref to access latest version without recreating debounced function
    const configRef = useRef(config);
    configRef.current = config;

    // Track whether filters had meaningful values previously. Seed this from the mount filters so
    // an immediate user clear (before the initial debounce fires) still reports the transition to
    // an empty where clause instead of silently cancelling the mount replay.
    const hadMeaningfulValuesRef = useRef(filters.some(hasFilterMeaningfulValue));

    // Create debounced update function - no dependencies to keep it stable
    const updateWhereClause = useMemo(
        () =>
            debounce((newFilters: Filter[]) => {
                const newWhereClause = buildWhereClause(newFilters, configRef.current);
                setWhereClause(newWhereClause);

                // Check if any filter has a meaningful value
                const hasMeaningfulValues = newFilters.some(hasFilterMeaningfulValue);

                // Only call onChange if:
                // 1. We have meaningful values now, OR
                // 2. We had meaningful values before but not anymore (clearing filters)
                const shouldCallOnChange = hasMeaningfulValues || hadMeaningfulValuesRef.current;

                // Update the ref for next comparison
                hadMeaningfulValuesRef.current = hasMeaningfulValues;

                // Call onChange here, after debounce. `interacted` is read at call time: when a
                // quick user edit collapses into the same debounce window as the mount replay,
                // the single trailing call correctly reports it as a user change.
                if (onChangeRef.current && shouldCallOnChange) {
                    onChangeRef.current(
                        newWhereClause,
                        computeSearchTerms(newFilters, configRef.current),
                        interactedRef.current,
                    );
                }
            }, 300), // 300ms delay
        [], // Empty deps - function is completely stable
    );

    // Rebuild when either the filters or their effective configuration changes. Dynamic configs
    // can gain viewer-specific transforms after auth hydration while the filter values themselves
    // remain unchanged (for example, resolving the stored "Current user" sentinel).
    useEffect(() => {
        updateWhereClause(filters);
    }, [config, filters, updateWhereClause]);

    // Compute searchTerms from textSearch filters
    const searchTerms = useMemo(() => computeSearchTerms(filters, config), [filters, config.filters]);

    // Replace the entire filter set in one shot (e.g. applying a saved view).
    // Resets the meaningful-values tracker so the debounced onChange always fires,
    // even when transitioning between two "empty" states.
    const setFilters = useCallback((next: Filter[]) => {
        interactedRef.current = true;
        hadMeaningfulValuesRef.current = true;
        setFiltersState(next);
    }, []);

    return {
        filters,
        addFilter,
        setFilters,
        updateFilter,
        removeFilter,
        clearFilters,
        whereClause,
        isFilterEmpty: filters.length === 0,
        searchTerms,
    };
}

/**
 * Hook for managing filter state with URL persistence
 *
 * Extension of useFilterBuilder that automatically syncs filter state
 * with URL query parameters. Useful for shareable filtered views.
 *
 * @template TWhere - The Prisma where input type
 * @param config - Filter configuration with field definitions
 * @param urlKey - Query parameter key for storing filters
 * @param initialFilters - Optional initial filter state (overridden by URL)
 * @param onChange - Optional callback when filter state changes
 * @param deepLinkSearchKey - Search parameter supplying initialFilters when it differs from urlKey
 * @returns Filter builder state with URL persistence
 *
 * @example
 * ```typescript
 * function ProductList() {
 *   const filterConfig = createFilterConfig<ProductWhere>([
 *     commonFields.text({ id: 'search', label: 'Search', dbField: 'name' }),
 *     commonFields.multiselect({
 *       id: 'categories',
 *       label: 'Categories',
 *       options: categoryOptions,
 *       customWhereBuilder: customWhereBuilders.prismaArrayField('categories')
 *     })
 *   ]);
 *
 *   // Filters will be synced to URL: ?product-filters=[{...}]
 *   const filterState = useFilterBuilderWithUrl(filterConfig, 'product-filters');
 *
 *   const { data } = trpc.product.list.useQuery({
 *     where: filterState.whereClause
 *   });
 *
 *   return (
 *     <>
 *       <FilterBar {...filterState} config={filterConfig} />
 *       <ProductTable data={data} />
 *     </>
 *   );
 * }
 * ```
 *
 * @remarks
 * - Filter state is automatically encoded/decoded from URL
 * - Changes to filters update the URL without page reload
 * - Shareable URLs maintain filter state
 * - Back/forward navigation preserves filters
 */
export function useFilterBuilderWithUrl<TWhere>(
    config: FilterConfig<TWhere>,
    urlKey: string,
    initialFilters?: Filter[],
    onChange?: (whereClause: TWhere, searchTerms?: string[]) => void,
    deepLinkSearchKey: string = urlKey,
): FilterBuilderState<TWhere> {
    const { pathname } = useLocation();
    const { history } = useRouter();

    // A non-empty `initialFilters` is an explicit deep link (e.g. a Home-card click put filters in
    // the URL). It must win over state stored by an earlier visit in this tab session — otherwise
    // the landing silently shows the stale filter set and the caller's onChange never receives the
    // linked filters (chips and table both wrong; seen on staging where no other seed masks it).
    // EXCEPT on a genuine drill-down-and-back (list → own sub-route → back): the URL still carries
    // the original deep-link params, but the user's restored working state must win there — the
    // same entry-context rule saved views apply (see useTableViews / isReturnFromSubRoute).
    // Applied deep-link filters are then persisted like any user state, so a later plain return
    // visit restores them under the normal rules below.
    const [hasDeepLinkFilters] = useState(
        () =>
            !isWhereClauseEmpty(buildInitialWhereFromFilters(config, initialFilters)) &&
            !isReturnFromSubRoute(getPreviousPathname(), pathname),
    );

    // Detect restoration (return visit) vs new navigation, so we can skip the restore replay.
    const hadStoredState = useRef(!hasDeepLinkFilters && hasStoredPathState(pathname, urlKey));

    const [persistedFilters, setPersistedFilters] = usePathState<Filter[]>(urlKey, initialFilters || []);
    const mountFilters = useRef(hasDeepLinkFilters ? (initialFilters ?? []) : persistedFilters).current;

    // Filter query parameters are one-shot entry instructions. Once this mount has accepted the
    // deep link, remove its parameter from the current history entry so a reload restores the
    // user's subsequently edited path state rather than re-applying the stale link.
    useEffect(() => {
        if (!hasDeepLinkFilters) return;

        const current = history.location;
        const search = new URLSearchParams(current.search);
        if (!search.has(deepLinkSearchKey)) return;

        search.delete(deepLinkSearchKey);
        const searchString = search.toString();
        history.replace(`${current.pathname}${searchString ? `?${searchString}` : ''}${current.hash}`, current.state);
    }, [deepLinkSearchKey, hasDeepLinkFilters, history]);

    // Skip ONLY the mount-time replay of restored filters (`interacted === false` on a return
    // visit) — its where/pagination were restored with the rest of the page's path state, and
    // re-applying it would reset the restored offset. Every user-driven change is forwarded.
    // Skipping "the first onChange call" instead used to swallow the user's first filter change
    // whenever the restored filters were empty: the replay never fires for meaningless filters
    // (see `shouldCallOnChange` in useFilterBuilder), so the first call to arrive was the user's
    // edit — chips updated but the query's where clause never did (CUR-451).
    const wrappedOnChange = useCallback(
        (whereClause: TWhere, searchTerms?: string[], interacted?: boolean) => {
            if (!interacted && hadStoredState.current) {
                return;
            }

            onChange?.(whereClause, searchTerms);
        },
        [onChange],
    );

    // Use the base hook with the mount-time filters and wrapped onChange
    const filterState = useFilterBuilder(config, mountFilters, wrappedOnChange);

    // Sync changes back to path state
    useEffect(() => {
        setPersistedFilters(filterState.filters);
    }, [filterState.filters, setPersistedFilters]);

    return filterState;
}
